// src/components/HomePage.tsx
import React, { Suspense, useEffect, useState } from 'react';
import { HomeHeader } from './layout/HomeHeader';
import { NavigationButtons } from './NavigationButtons';
import { LoadingIndicator } from './shared/LoadingIndicator';
import { FirstVisitBanner } from './pwa/FirstVisitBanner';
import { StayDateSelector } from './stay/StayDateSelector';
import { useDevice } from '../hooks/useDevice';
import { useHotelConfig } from '../hooks/useHotelConfig';
import { resolveColor } from '../utils/colorUtils';
import { stayService } from '../lib/stay/stayService';
import { useQuery } from '@tanstack/react-query';
import { configClient } from '../lib/supabase/configClient';

interface HomePageProps {
  onLivechatClick: (url: string) => void;
  onSocialNetworksClick: () => void;
  messageCount: number;
}

export const HomePage: React.FC<HomePageProps> = ({ 
  onLivechatClick, 
  onSocialNetworksClick,
  messageCount 
}) => {
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showStaySelector, setShowStaySelector] = useState(false);
  const { os, isDesktop } = useDevice();
  const { id: hotelId, baseUrl } = useHotelConfig();

  // Fetch config directly
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: () => {
      return configClient.getConfig();
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  // Détecter le mode PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    const hasVisited = localStorage.getItem('has_visited');
    if (!hasVisited && !isPWA) {
      setShowInstallBanner(true);
    }
  }, [isPWA, hotelId, baseUrl]);

  // Check if we need to show the stay selector
  useEffect(() => {
    if (config) {
      const hasSavedDates = stayService.hasStayDates();
      const areDatesExpired = stayService.areDatesExpired();

      const shouldShow = config.open_with_stay === true && (!hasSavedDates || areDatesExpired);
      setShowStaySelector(shouldShow);
    }
  }, [config]);

  useEffect(() => {
    if (config) {
      if (config.image) {
        document.body.style.backgroundImage = `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${config.image})`;
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundRepeat = 'no-repeat';
        document.body.style.backgroundAttachment = 'fixed';
      } else {
        const backgroundColor = resolveColor(config['homeBack-color'], config, config['medium-color']);
        document.body.style.backgroundColor = backgroundColor;
        document.body.style.backgroundImage = 'none';
      }
    }

    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundColor = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
    };
  }, [config]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement des données</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (isLoading || !config) {
    return <LoadingIndicator id="homepage-loader" />;
  }

  const handleStayDatesSelected = () => {
    setShowStaySelector(false);

    if (!localStorage.getItem('has_visited') && !isPWA) {
      setShowInstallBanner(true);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        backgroundColor: !config.image ? resolveColor(config['homeBack-color'], config, config['medium-color']) : undefined 
      }}
    >
      <HomeHeader 
        config={config}
        title={config.name}
      />
      <div className="home-content flex flex-col min-h-[calc(100vh-50px)]">
        <NavigationButtons 
          onLivechatClick={onLivechatClick} 
          onSocialNetworksClick={onSocialNetworksClick}
          messageCount={messageCount}
        />
      </div>

      {showInstallBanner && !isPWA && (
        <FirstVisitBanner
          config={config}
          onClose={() => setShowInstallBanner(false)}
          platform={os === 'iOS' ? 'ios' : 'android'}
        />
      )}

      {showStaySelector && (
        <StayDateSelector
          config={config}
          onDatesSelected={handleStayDatesSelected}
          isInitialSetup={true}
          canClose={false}
        />
      )}
    </div>
  );
};
